import { JSX } from "react";
import { motion } from "motion/react";
import { 
    ClockIcon, 
    ArchiveBoxIcon, 
    ExclamationTriangleIcon,
    CurrencyDollarIcon,
    UserGroupIcon,
    DocumentDuplicateIcon
} from "@heroicons/react/24/outline";

const styles = {
    section: "bg-gray-50 py-20",
    container: "mx-auto max-w-7xl px-6 lg:px-8",
    header: "mx-auto max-w-2xl text-center mb-16",
    title: "text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl",
    subtitle: "mt-6 text-lg text-gray-600",
    grid: "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3",
    card: "p-6 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center transition-all duration-300 hover:shadow-lg hover:border-red-100 hover:-translate-y-1",
    iconWrap: "p-4 bg-red-50 rounded-2xl mb-4 transition-colors duration-300 group-hover:bg-red-100",
    icon: "h-8 w-8 text-red-500",
    cardTitle: "text-lg font-semibold text-gray-900 mb-3",
    cardDesc: "text-gray-600 text-sm leading-relaxed",
};

const painPoints = [
    {
        title: "Citas Olvidadas",
        desc: "Los huecos en la agenda por inasistencias afectan tus ingresos y la salud de los pacientes.",
        icon: <ClockIcon className={styles.icon} />,
    },
    {
        title: "Caos Administrativo",
        desc: "Perder tiempo buscando historiales en papel o archivos dispersos ralentiza tu atención.",
        icon: <ArchiveBoxIcon className={styles.icon} />,
    },
    {
        title: "Estrés Operativo",
        desc: "La carga de recordatorios manuales y la gestión de vacunas se vuelve abrumadora.",
        icon: <ExclamationTriangleIcon className={styles.icon} />,
    },
    {
        title: "Pérdida de Ingresos",
        desc: "Las cancelaciones de último minuto y los tiempos muertos en tu agenda impactan directamente tu facturación.",
        icon: <CurrencyDollarIcon className={styles.icon} />,
    },
    {
        title: "Clientes Disconformes",
        desc: "La falta de seguimiento genera frustración en los dueños de mascotas que sienten que su atención es deficiente.",
        icon: <UserGroupIcon className={styles.icon} />,
    },
    {
        title: "Historiales Incompletos",
        desc: "Sin un registro digital unificado, es fácil perder información crítica de cada paciente.",
        icon: <DocumentDuplicateIcon className={styles.icon} />,
    },
];

const ProblemSection = (): JSX.Element => (
    <section className={styles.section}>
        <div className={styles.container}>
            <div className={styles.header}>
                <motion.h2 
                    className={styles.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    ¿Tu clínica se siente como un caos constante?
                </motion.h2>
                <motion.p 
                    className={styles.subtitle}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    viewport={{ once: true }}
                >
                    Sabemos que amas cuidar a los animales, pero la gestión administrativa puede ser agotadora. Estos son los problemas más comunes que enfrentamos los veterinarios.
                </motion.p>
            </div>

            <div className={styles.grid}>
                {painPoints.map((point, index) => (
                    <motion.div 
                        key={point.title} 
                        className={styles.card}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.08 }}
                        viewport={{ once: true }}
                    >
                        <div className={styles.iconWrap}>{point.icon}</div>
                        <h3 className={styles.cardTitle}>{point.title}</h3>
                        <p className={styles.cardDesc}>{point.desc}</p>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

export default ProblemSection;